/**
 * Splits and joins, one after another, without making the operator wait to click.
 *
 * A split or a join rewrites whole documents (pages, fields, table rows), so it
 * cannot run beside another save to the same documents, and a document held as
 * a skeleton has to be loaded in full first. The controls used to refuse the
 * click while anything like that was going on and say why ("Saving a value on
 * this document - a moment"). Now the click is accepted at once and waits here
 * for its turn: whatever is saving on those documents finishes, then the change
 * is applied and saved, then the next one.
 *
 * A request names the PAGE it is about, never a position or a document id.
 * Pages keep their batch number for life; documents and positions do not (the
 * split before this one moved every later position). So "start a new document
 * at page 14" still means the same thing after the changes queued ahead of it
 * have landed, and a request that no longer makes sense (the page already
 * starts a document) is simply dropped rather than applied somewhere else.
 *
 * Only ordering lives here. Whether it is a request's turn, and what applying
 * it means, belong to the workspace that owns the save lanes; they are passed in.
 */

/**
 * The engine refuses a boundary change built from documents that changed after
 * they were loaded, with this code in its message. Nothing was written; the
 * change is rebuilt from the documents as they are now and tried again.
 */
export const BOUNDARY_REVISION_CHANGED = 'boundary-revision-changed';

/**
 * How long the documents a boundary change touched wait for the operator's next
 * cut before they are read again. A run of quick cuts is saved first and read
 * once, instead of each re-read holding up the cut after it.
 */
export const BOUNDARY_RECHECK_SETTLE_MS = 1500;

/**
 * A cut saved this soon after the click takes the operator to the new document,
 * as it always has. One that had to wait longer leaves them where they are: by
 * then they may be reading or typing, and the screen must not move under them.
 */
export const BOUNDARY_FOLLOW_MS = 1500;

/** Where the operator asked for a boundary to change. */
export type BoundaryRequest =
  /** `pageNumber` starts a new document. */
  | { kind: 'split'; pageNumber: number }
  /** The document opening at `pageNumber` joins the one before it. */
  | { kind: 'join'; pageNumber: number }
  /** Put back the split or join queued as item `changeId`, once it has been saved. */
  | { kind: 'undo'; pageNumber: number | null; changeId: number }
  /** Re-read the documents the changes just saved touched. Always last. */
  | { kind: 'recheck' };

export type BoundaryQueueItem = BoundaryRequest & {
  id: number;
  /** Waiting for its turn, or being applied and saved now. */
  phase: 'waiting' | 'saving';
  /** The document on screen when the operator asked, so the view can follow the cut. */
  viewedDocumentId: string | null;
  /** When the operator asked (Date.now()). */
  requestedAt: number;
};

/** Is it this item's turn? `capture` is waiting only for pages being read to land. */
export type BoundaryTurn = 'ready' | 'wait' | 'capture';

export type BoundaryQueueOwner = {
  turn(item: BoundaryQueueItem): BoundaryTurn;
  /**
   * Apply and save one item. It must claim its documents before its first await;
   * `retry` means the claim was lost to another writer in the same moment, so
   * the item goes back to waiting instead of being dropped.
   */
  run(item: BoundaryQueueItem): Promise<'done' | 'retry'>;
  /** Resolves when a save lane may have changed (or after a short poll). */
  nextChange(): Promise<void>;
  changed?(items: readonly BoundaryQueueItem[], waitingForCapture: boolean): void;
};

type PagedDocument = { id: string; pages: readonly { number: number }[] };

/** The document and page holding a batch page number, or null when no document has it. */
export function locateBatchPage(documents: readonly PagedDocument[], pageNumber: number) {
  for (let documentIndex = 0; documentIndex < documents.length; documentIndex++) {
    const pageIndex = documents[documentIndex].pages.findIndex(
      (page) => page.number === pageNumber,
    );
    if (pageIndex >= 0) return { documentIndex, pageIndex };
  }
  return null;
}

export type ResolvedBoundaryRequest =
  | { kind: 'split'; documentIndex: number; pageIndex: number; documentIds: [string] }
  | { kind: 'join'; documentIndex: number; documentIds: [string, string] }
  | { kind: 'none'; reason: 'missing' | 'already' };

/**
 * What a split or join means against the batch as it is now. `already` is the
 * operator's wish being true already (the page starts a document, or no longer
 * does), which is not a failure and needs no word.
 */
export function resolveBoundaryRequest(
  documents: readonly PagedDocument[],
  request: { kind: 'split' | 'join'; pageNumber: number },
): ResolvedBoundaryRequest {
  const at = locateBatchPage(documents, request.pageNumber);
  if (!at) return { kind: 'none', reason: 'missing' };
  if (request.kind === 'split')
    return at.pageIndex > 0
      ? {
          kind: 'split',
          documentIndex: at.documentIndex,
          pageIndex: at.pageIndex,
          documentIds: [documents[at.documentIndex].id],
        }
      : { kind: 'none', reason: 'already' };
  return at.pageIndex === 0 && at.documentIndex > 0
    ? {
        kind: 'join',
        documentIndex: at.documentIndex,
        documentIds: [documents[at.documentIndex - 1].id, documents[at.documentIndex].id],
      }
    : { kind: 'none', reason: 'already' };
}

export class BoundaryQueue {
  private items: BoundaryQueueItem[] = [];
  private snapshotItems: readonly BoundaryQueueItem[] = [];
  private listeners = new Set<() => void>();
  private nextId = 1;
  private running = false;
  private waitingForCapture = false;

  private readonly owner: BoundaryQueueOwner;

  constructor(owner: BoundaryQueueOwner) {
    this.owner = owner;
  }

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };
  getSnapshot = () => this.snapshotItems;
  get size() {
    return this.items.length;
  }
  /** Whether the item at the front waits only for the pages being read to land. */
  get headWaitsForCapture() {
    return this.waitingForCapture;
  }

  /**
   * Queue an operator's split, join or undo. A second click on a page that
   * already has a change waiting is the same request, not a new one. New
   * requests go ahead of a recheck that has not started, so a run of quick cuts
   * is saved first and re-read once.
   */
  enqueue(
    request: Exclude<BoundaryRequest, { kind: 'recheck' }>,
    viewedDocumentId: string | null = null,
  ) {
    if (
      request.kind !== 'undo' &&
      this.items.some(
        (item) =>
          item.kind !== 'recheck' &&
          item.kind === request.kind &&
          item.pageNumber === request.pageNumber,
      )
    )
      return null;
    const item: BoundaryQueueItem = {
      ...request,
      id: this.nextId++,
      phase: 'waiting',
      viewedDocumentId,
      requestedAt: Date.now(),
    };
    const recheckAt = this.items.findIndex(
      (candidate) => candidate.kind === 'recheck' && candidate.phase === 'waiting',
    );
    if (recheckAt >= 0) this.items.splice(recheckAt, 0, item);
    else this.items.push(item);
    this.publish();
    void this.pump();
    return item;
  }

  /** Ask for one re-read after the changes in front of it. Several asks share one. */
  requestRecheck() {
    if (!this.items.some((item) => item.kind === 'recheck' && item.phase === 'waiting')) {
      this.items.push({
        kind: 'recheck',
        id: this.nextId++,
        phase: 'waiting',
        viewedDocumentId: null,
        requestedAt: Date.now(),
      });
      this.publish();
    }
    void this.pump();
  }

  /**
   * Take back the newest split or join that has not started saving. Returns it,
   * or null when the newest request is already being saved, or is itself an
   * undo (an undo then has to wait for it and put it back properly).
   */
  cancelNewestWaiting() {
    for (let index = this.items.length - 1; index >= 0; index--) {
      const item = this.items[index];
      if (item.kind === 'recheck') continue;
      if (item.phase !== 'waiting' || item.kind === 'undo') return null;
      this.items.splice(index, 1);
      this.publish();
      return item;
    }
    return null;
  }

  private publish() {
    this.snapshotItems = this.items.map((item) => ({ ...item }));
    this.owner.changed?.(this.snapshotItems, this.waitingForCapture);
    for (const listener of this.listeners) listener();
  }

  private setWaitingForCapture(value: boolean) {
    if (this.waitingForCapture === value) return;
    this.waitingForCapture = value;
    this.publish();
  }

  private async pump() {
    if (this.running) return;
    this.running = true;
    try {
      while (this.items.length > 0) {
        const head = this.items[0];
        const turn = this.owner.turn(head);
        if (turn !== 'ready') {
          this.setWaitingForCapture(turn === 'capture');
          await this.owner.nextChange();
          continue;
        }
        this.setWaitingForCapture(false);
        head.phase = 'saving';
        this.publish();
        let outcome: 'done' | 'retry' = 'done';
        try {
          outcome = await this.owner.run(head);
        } catch {
          // run() reports its own failures; an escaped error must not stop the
          // requests behind this one from being saved.
          outcome = 'done';
        }
        if (outcome === 'retry' && this.items[0] === head) {
          head.phase = 'waiting';
          this.publish();
          await this.owner.nextChange();
          continue;
        }
        const at = this.items.indexOf(head);
        if (at >= 0) this.items.splice(at, 1);
        this.publish();
      }
    } finally {
      this.running = false;
      this.setWaitingForCapture(false);
    }
  }
}
